// UseCaseContext.tsx
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useCallback,
} from "react";
import { useLogin } from "../../utils/authConfig";
import { getToken } from "../../utils/authConfig";
import { useMsal } from "@azure/msal-react";
import { configApi, Config, ToolInfo, SiteMappingOption } from "../../api";

export interface UseCase {
  id: string;
  name: string;
  showUserUpload: boolean;
  showModelSettings: boolean;
  availableModels: string[];
  availableTools: ToolInfo[];
  siteMappings: SiteMappingOption[];
}

export interface UseCaseMap {
  [id: string]: UseCase;
}

interface UseCaseContextType {
  useCases: UseCaseMap;
  selectedUseCase: string;
  setSelectedUseCase: (useCase: string) => void;
  reloadUseCases: () => Promise<void>;
  loading: boolean;
}

const UseCaseContext = createContext<UseCaseContextType | undefined>(undefined);

const EMPTY_CONFIG: Config = {
  enabledUseCases: {},
  modelSettingsAllowed: {},
  availableModels: {},
  availableTools: {},
  siteMappings: {},
};

export const UseCaseProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const client = useLogin ? useMsal().instance : undefined;

  const [useCases, setUseCases] = useState<UseCaseMap>({});
  const [selectedUseCase, setSelectedUseCase] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const getConfig = useCallback(async (): Promise<Config> => {
    try {
      const token = client ? await getToken(client) : undefined;
      if (!token) return EMPTY_CONFIG;

      return await configApi(token);
    } catch (error) {
      console.error("Error fetching config:", error);
      return EMPTY_CONFIG;
    }
  }, [client]);

  // Concurrent reloads share one request.
  const inFlight = useRef<Promise<void> | null>(null);

  const loadUseCases = useCallback(async () => {
    setLoading(true);

    try {
      const data = await getConfig();

      const loadedUseCases: UseCaseMap = Object.keys(
        data.enabledUseCases
      ).reduce((acc, id) => {
        acc[id] = {
          id,
          name: id,
          showUserUpload: data.enabledUseCases[id]?.showUserUpload ?? false,
          showModelSettings: data.modelSettingsAllowed[id] ?? false,
          availableModels: data.availableModels[id] ?? [],
          availableTools: data.availableTools[id] ?? [],
          siteMappings: data.siteMappings[id] ?? [],
        };
        return acc;
      }, {} as UseCaseMap);

      setUseCases(loadedUseCases);

      const list = Object.values(loadedUseCases);

      setSelectedUseCase((prev) => {
        if (prev !== "") return prev;

        if (list.length === 0) return "";

        return loadedUseCases["GENERIC"]
          ? "GENERIC"
          : list[0].name;
      });
    } catch (error) {
      console.error("Error loading use cases:", error);
      setUseCases({});
    } finally {
      setLoading(false);
    }
  }, [getConfig]);

  const reloadUseCases = useCallback(() => {
    if (!inFlight.current) {
      inFlight.current = loadUseCases().finally(() => {
        inFlight.current = null;
      });
    }
    return inFlight.current;
  }, [loadUseCases]);

  // The configuration is loaded once per page load; switching use cases doesn't reload it.
  useEffect(() => {
    reloadUseCases();
  }, [reloadUseCases]);

  return (
    <UseCaseContext.Provider
      value={{
        useCases,
        selectedUseCase,
        setSelectedUseCase,
        reloadUseCases,
        loading,
      }}
    >
      {children}
    </UseCaseContext.Provider>
  );
};

export const useUseCase = (): UseCaseContextType => {
  const context = useContext(UseCaseContext);
  if (!context) {
    throw new Error("useUseCase must be used within a UseCaseProvider");
  }
  return context;
};
